import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { tokens, Button } from '@hotjar/hotsauce';

import type { HighlightType } from '@hotjar/webapp/domains/highlights/model';
import { SkeletonLoader } from './SkeletonLoader';

const Loading = styled(SkeletonLoader)`
  width: 153px;
  height: 32px;
  margin: ${tokens.spacer.x1};
  position: absolute;
  top: 0px;
`;

const StyledButton = styled(Button)`
  ${tokens.text('caption')};

  &,
  &:focus,
  &:hover {
    background-color: ${tokens.color.ui_overlay};
    color: ${tokens.color.text_default_inverse};
  }
`;

interface FullItemLinkProps {
  className?: string;
  highlightType?: HighlightType;
  url?: string;
}

export function FullItemLink({
  className,
  highlightType,
  url,
}: FullItemLinkProps): JSX.Element | null {
  const { t } = useTranslation();

  // eslint-disable-next-line consistent-return
  function getLabelAndIcon(): [string | null, React.ComponentProps<typeof Button>['icon'] | null] {
    // eslint-disable-next-line default-case
    switch (highlightType) {
      case 'heatmap':
        return [t('highlights:heatmaps.open-full'), 'heatmaps'];
      case 'recording':
        return [t('highlights:recordings.view-full'), 'play_alt'];
      default:
        return [t('highlights:unknown.view-full'), 'rocketship'];
    }
  }

  const [label, icon] = getLabelAndIcon();

  if (!label || !icon) {
    return null;
  }

  if (!highlightType || !url) {
    return (
      <Loading
        shimmerColor={tokens.color.neutral_700}
        shimmerDarkColor={tokens.color.neutral_800}
      />
    );
  }

  return (
    <StyledButton
      as="a"
      className={className}
      href={`${url}&opener=highlight`}
      icon={icon}
      isExternal
      kind="secondary"
      size="s"
    >
      {label}
    </StyledButton>
  );
}
